import { useState, useEffect, useRef } from 'react';
import { Input } from '../ui/input';
import { MapPin, Plus, ChevronDown, ExternalLink } from 'lucide-react';
import api from '../../services/api';

export const LocationComboBox = ({ value, locationUrl, onChange, onLocationUrlChange }) => {
  const [locations, setLocations] = useState([]);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState(value || '');
  const [saving, setSaving] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    api.get('/game-locations').then(res => setLocations(res.data)).catch(() => {});
  }, []);

  useEffect(() => { setSearch(value || ''); }, [value]);

  // Close dropdown on outside click - use pointerdown for mobile compatibility
  useEffect(() => {
    const handleClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('pointerdown', handleClick);
    return () => document.removeEventListener('pointerdown', handleClick);
  }, []);

  const filtered = locations.filter(l =>
    l.name.toLowerCase().includes(search.toLowerCase())
  );
  const exactMatch = locations.find(l => l.name.toLowerCase() === search.trim().toLowerCase());

  const selectLocation = (loc) => {
    onChange(loc.name);
    onLocationUrlChange(loc.google_maps_url || '');
    setSearch(loc.name);
    setOpen(false);
  };

  const handleSaveNew = async () => {
    if (!search.trim()) return;
    setSaving(true);
    try {
      const res = await api.post('/game-locations', {
        name: search.trim(),
        google_maps_url: locationUrl || null
      });
      setLocations(prev => [...prev, res.data].sort((a, b) => a.name.localeCompare(b.name)));
      onChange(res.data.name);
      setOpen(false);
    } catch { /* ignore */ }
    finally { setSaving(false); }
  };

  const handleInputChange = (e) => {
    const val = e.target.value;
    setSearch(val);
    onChange(val);
    if (!open && val) setOpen(true);
  };

  return (
    <div ref={wrapperRef} className="space-y-2">
      <div className="relative">
        <div className="relative">
          <MapPin className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
          <Input
            value={search}
            onChange={handleInputChange}
            onFocus={() => setOpen(true)}
            placeholder="Type or select a location..."
            className="pl-8 pr-8"
            data-testid="game-location-input"
          />
          <button
            type="button"
            onClick={() => setOpen(!open)}
            className="absolute right-2 top-2.5 text-slate-400 hover:text-slate-600"
          >
            <ChevronDown className={`h-4 w-4 transition-transform ${open ? 'rotate-180' : ''}`} />
          </button>
        </div>

        {open && (
          <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-[220px] overflow-y-auto">
            {filtered.length > 0 ? (
              filtered.map(loc => (
                <button
                  key={loc.id}
                  type="button"
                  onPointerDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    selectLocation(loc);
                  }}
                  className="w-full text-left px-3 py-2.5 hover:bg-slate-50 active:bg-slate-100 flex items-center justify-between gap-2 border-b border-slate-50 last:border-0"
                  data-testid={`location-option-${loc.id}`}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <MapPin className="h-3.5 w-3.5 text-slate-400 flex-shrink-0" />
                    <span className="text-sm truncate">{loc.name}</span>
                  </div>
                  {loc.google_maps_url && (
                    <ExternalLink className="h-3 w-3 text-blue-400 flex-shrink-0" />
                  )}
                </button>
              ))
            ) : search.trim() ? (
              <div className="px-3 py-2 text-sm text-slate-500">No matching locations</div>
            ) : (
              <div className="px-3 py-2 text-sm text-slate-500">No saved locations yet</div>
            )}

            {/* Save new option */}
            {search.trim() && !exactMatch && (
              <button
                type="button"
                onPointerDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleSaveNew();
                }}
                disabled={saving}
                className="w-full text-left px-3 py-2.5 bg-emerald-50 hover:bg-emerald-100 active:bg-emerald-200 flex items-center gap-2 border-t border-slate-200 text-emerald-700"
                data-testid="save-new-location-btn"
              >
                <Plus className="h-3.5 w-3.5" />
                <span className="text-sm font-medium">
                  {saving ? 'Saving...' : `Save "${search.trim()}" as new location`}
                </span>
              </button>
            )}
          </div>
        )}
      </div>

      {/* Google Maps URL */}
      <div className="relative">
        <ExternalLink className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
        <Input
          value={locationUrl}
          onChange={(e) => onLocationUrlChange(e.target.value)}
          placeholder="Google Maps URL (auto-filled or paste here)"
          className="pl-8"
          data-testid="game-location-url-input"
        />
      </div>
      {locationUrl && (
        <a href={locationUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800">
          <MapPin className="h-3 w-3" />Open in Google Maps
        </a>
      )}
    </div>
  );
};
